import { RefObject, useCallback, useLayoutEffect } from 'react';
import noop from 'lodash/noop';
import debounce from 'lodash/debounce';

export interface UseMutationsOptions {
  active?: boolean;
  delay?: number;
  init: MutationObserverInit;
}

const useMutations = <T extends Element>(ref: RefObject<T>, callback: () => void, { active = true, delay = 50, init }: UseMutationsOptions) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleCallback = useCallback(() => callback(), []);

  useLayoutEffect(() => {
    const element = ref?.current;
    if (!element || !active) {
      return noop;
    }

    handleCallback();

    const debouncedCallback = debounce(handleCallback, delay);
    const mutationObserver = new MutationObserver(handleCallback);
    mutationObserver.observe(element, init);

    return () => {
      debouncedCallback.cancel();
      mutationObserver.disconnect();
    };
  }, [active, ref, delay, handleCallback, init]);
};

export default useMutations;
